/**
 * ViewJobsSection — My Jobs page. Mirrors Flutter WebViewJobs + its tabs:
 *   lib/presentation/web_profile/widgets/web_view_jobs.dart (tabs, Applied filter)
 *   web_applied_jobs.dart / web_invited_jobs.dart / web_recommended_jobs.dart / web_saved_jobs.dart
 *
 * Layout follows the Career Portal redesign: job list + sticky preview of the selected job
 * (desktop). Below the split breakpoint selecting a job opens its page and the cards carry the
 * apply actions, as in Flutter. Mobile follows Flutter's isMobile branch: button tabs that
 * scroll with arrows, no Applied filter dropdown, and a compact Recommended filter row.
 * More jobs load on scroll (Flutter's scroll-controller listeners).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useViewJobs, ALL_TABS } from '../hooks/useViewJobs';
import { ProfileJobCard, ApplicationActions, ApplicationStatusPill } from './ProfileJobCard';
import { HomeJobDetailPanel } from '@/features/home/components/HomeJobDetailPanel';
import { useIsMobile, useIsSplitLayout } from '@/hooks/useMediaQuery';
import { DataPrivacyDialog } from '@/features/home/components/DataPrivacyDialog';
import { Toast } from '@/features/home/components/Toast';
import { FilterDropdown, SortDropdown } from '@/features/jobs/components/JobFilters';
import { getRecommendedFilterKeys, withCheckedState } from '@/features/jobs/utils/filterState';
import { isAgreedWithDataPolicy, setAgreedWithDataPolicy } from '@/features/jobs/utils/resumeFile';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useTenantStore } from '@/store/tenantStore';
import { ROUTES } from '@/app/router/routes';
import { FILE_ACCEPT_STRING } from '@/types/application.types';
import { ViewJobsTab } from '@/types/profile.types';
import type { AppliedJobsFilter, ViewJobsData } from '@/types/profile.types';
import styles from './ViewJobsSection.module.css';

/** Flutter WebProfileConstants.jobsTabTitle */
const TAB_LABELS: Record<ViewJobsTab, string> = {
  [ViewJobsTab.APPLIED]: 'My Jobs',
  [ViewJobsTab.INVITED]: 'Invited Jobs',
  [ViewJobsTab.RECOMMENDED]: 'Recommended Jobs',
  [ViewJobsTab.SAVED]: 'Saved Jobs',
};

const APPLIED_FILTERS: AppliedJobsFilter[] = ['All', 'Pending', 'Applied'];

/** Flutter NoJobFoundWidget contentText per tab */
const EMPTY_TEXT: Record<ViewJobsTab, string> = {
  [ViewJobsTab.APPLIED]: 'You have not applied to any jobs',
  [ViewJobsTab.INVITED]: 'No invited jobs',
  [ViewJobsTab.RECOMMENDED]: 'No recommended jobs',
  [ViewJobsTab.SAVED]: 'No saved jobs',
};

/** Flutter JobsListViewShimmer itemCount per tab */
const SHIMMER_COUNT: Record<ViewJobsTab, number> = {
  [ViewJobsTab.APPLIED]: 3,
  [ViewJobsTab.INVITED]: 3,
  [ViewJobsTab.RECOMMENDED]: 3,
  [ViewJobsTab.SAVED]: 4,
};

function getTabFromPath(pathname: string): ViewJobsTab {
  if (pathname.includes('invited')) return ViewJobsTab.INVITED;
  if (pathname.includes('recommended')) return ViewJobsTab.RECOMMENDED;
  if (pathname.includes('saved')) return ViewJobsTab.SAVED;
  return ViewJobsTab.APPLIED;
}

/** Flutter WebAppliedJobs.getFilteredJobs */
function filterAppliedJobs(jobs: ViewJobsData[], filter: AppliedJobsFilter): ViewJobsData[] {
  if (filter === 'Pending') return jobs.filter((j) => !j.appliedStatus || j.appliedStatus === 'DRAFT');
  if (filter === 'Applied') return jobs.filter((j) => !!j.appliedStatus && j.appliedStatus !== 'DRAFT');
  return jobs;
}

export function ViewJobsSection() {
  const { t } = useTranslation();
  const location = useLocation();
  const preferences = useTenantStore((s) => s.preferences);
  const { state, actions } = useViewJobs(getTabFromPath(location.pathname));
  const isSplit = useIsSplitLayout();
  const isMobile = useIsMobile();

  const [appliedFilter, setAppliedFilter] = useState<AppliedJobsFilter>('All');
  const [selectedIds, setSelectedIds] = useState<Partial<Record<ViewJobsTab, string>>>({});

  const tab = state.activeTab;
  const list = state.lists[tab];
  const jobs = useMemo(
    () => (tab === ViewJobsTab.APPLIED ? filterAppliedJobs(list.jobs, appliedFilter) : list.jobs),
    [tab, list.jobs, appliedFilter],
  );

  // Keep a valid selection: default to the first job, and fall back when the list changes
  const selectedIndex = Math.max(
    0,
    jobs.findIndex((j) => (j.displayId ?? j.id) === selectedIds[tab]),
  );
  const selected = jobs[selectedIndex];

  const handleSelect = (job: ViewJobsData) => {
    if (!isSplit) {
      actions.openJob(tab, job);
      return;
    }
    const id = job.displayId ?? job.id;
    if (id) setSelectedIds((prev) => ({ ...prev, [tab]: id }));
  };

  // ── Browser back / forward between the tab URLs (a tab click pushes one) ──
  const { syncTabFromUrl } = actions;
  useEffect(() => {
    if (location.pathname.startsWith(`${ROUTES.VIEW_JOBS}/`)) {
      syncTabFromUrl(getTabFromPath(location.pathname));
    }
  }, [location.pathname, syncTabFromUrl]);

  // ── Infinite scroll (Flutter: load more at 60% of the tab's scroll extent) ──
  const { loadMore } = actions;
  useEffect(() => {
    const loadIfNearEnd = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 1000) loadMore();
    };
    window.addEventListener('scroll', loadIfNearEnd, { passive: true });
    // A page too short to scroll would never load the next page
    loadIfNearEnd();
    return () => window.removeEventListener('scroll', loadIfNearEnd);
  }, [loadMore, list.jobs.length, list.isLoading, list.isLoadingMore]);

  const emptyText =
    tab === ViewJobsTab.APPLIED && appliedFilter !== 'All' && list.jobs.length > 0
      ? 'No jobs found for the selected filter'
      : EMPTY_TEXT[tab];

  // Flutter shows the filter bar only above a non-empty list
  const showRecommendedFilters =
    tab === ViewJobsTab.RECOMMENDED &&
    !state.showUploadResume &&
    list.jobs.length > 0 &&
    (state.isLoadingAggregations || state.recommendedAggregations.length > 0);

  return (
    <div className={styles.container}>
      {/* Tabs + Applied filter (desktop only, as in Flutter) */}
      {isMobile ? (
        <MobileTabs active={tab} onSelect={actions.setActiveTab} />
      ) : (
        <div className={styles.toolbar}>
          <div className={styles.segment} role="tablist">
            {ALL_TABS.map((item) => (
              <button
                key={item}
                type="button"
                role="tab"
                aria-selected={tab === item}
                className={`${styles.segmentBtn} ${tab === item ? styles.segmentActive : ''}`}
                onClick={() => actions.setActiveTab(item)}
              >
                {t(TAB_LABELS[item])}
              </button>
            ))}
          </div>

          {tab === ViewJobsTab.APPLIED && (
            <select
              className={styles.select}
              value={appliedFilter}
              aria-label={t(TAB_LABELS[ViewJobsTab.APPLIED])}
              onChange={(e) => setAppliedFilter(e.target.value as AppliedJobsFilter)}
            >
              {APPLIED_FILTERS.map((f) => (
                <option key={f} value={f}>
                  {t(f)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {showRecommendedFilters && (
        <RecommendedFilters
          aggregations={state.recommendedAggregations}
          filters={state.recommendedFilters}
          preferences={preferences as Record<string, unknown> | null}
          isLoading={state.isLoadingAggregations}
          isMobile={isMobile}
          actions={actions}
        />
      )}

      {tab === ViewJobsTab.RECOMMENDED && state.showUploadResume ? (
        <UploadResumePrompt isUploading={state.isUploadingResume} onUpload={actions.uploadResume} />
      ) : list.isLoading ? (
        <div className={styles.split}>
          <div className={styles.list}>
            <JobListShimmer count={SHIMMER_COUNT[tab]} />
          </div>
          {isSplit && <div className={styles.shimmerDetail} aria-hidden="true" />}
        </div>
      ) : jobs.length === 0 ? (
        <EmptyState text={emptyText} />
      ) : (
        <div className={styles.split}>
          <div className={styles.listCol}>
            <div className={styles.list}>
              {jobs.map((job, index) => (
                <ProfileJobCard
                  // The lists can hold the same job twice (e.g. two applications, or an
                  // overlapping offset page); Flutter's ListView shows both, so keep both
                  key={`${job.displayId ?? job.id ?? ''}-${index}`}
                  job={job}
                  tab={tab}
                  isSelected={isSplit && index === selectedIndex}
                  onSelect={() => handleSelect(job)}
                  onSave={() => actions.saveJob(tab, job)}
                  onUnsave={() => actions.unsaveJob(tab, job)}
                  onApply={
                    isSplit ? undefined : (isEasyApply) => actions.openApplication(tab, job, isEasyApply)
                  }
                />
              ))}
              {list.isLoadingMore && <JobListShimmer count={2} />}
            </div>
          </div>

          {isSplit && selected && (
            <div className={styles.detailCol}>
              <HomeJobDetailPanel
                key={selected.displayId ?? selected.id}
                entity={selected}
                onSave={() => actions.saveJob(tab, selected)}
                onUnsave={() => actions.unsaveJob(tab, selected)}
                onClickApply={(isEasyApply) => actions.openApplication(tab, selected, isEasyApply)}
                onViewDetails={() => actions.openJob(tab, selected)}
                actions={
                  <>
                    <ApplicationStatusPill job={selected} />
                    <ApplicationActions
                      job={selected}
                      onApply={(isEasyApply) => actions.openApplication(tab, selected, isEasyApply)}
                    />
                  </>
                }
              />
            </div>
          )}
        </div>
      )}

      {state.toast && (
        <Toast message={state.toast.message} type={state.toast.type} onClose={actions.clearToast} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile tabs — Flutter _buildTabBarWidget (isMobile): JobTabItem(isButtonView) in a horizontal
// list, with back / forward arrows that scroll it by 100px
// ---------------------------------------------------------------------------

function MobileTabs({ active, onSelect }: { active: ViewJobsTab; onSelect: (tab: ViewJobsTab) => void }) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Flutter _Init: the Saved tab scrolls the tab list to its end
  useEffect(() => {
    const el = scrollRef.current;
    if (el && active === ViewJobsTab.SAVED) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollBy = (left: number) => scrollRef.current?.scrollBy({ left, behavior: 'smooth' });

  return (
    <div className={styles.mobileTabs}>
      <div ref={scrollRef} className={styles.mobileTabsScroll} role="tablist">
        {ALL_TABS.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={active === item}
            className={`${styles.mobileTab} ${active === item ? styles.mobileTabActive : ''}`}
            onClick={() => onSelect(item)}
          >
            {t(TAB_LABELS[item])}
          </button>
        ))}
      </div>
      {/* Scroll aids only — the tabs themselves stay keyboard reachable */}
      <button
        type="button"
        className={`${styles.tabArrow} ${styles.tabArrowBack}`}
        onClick={() => scrollBy(-100)}
        tabIndex={-1}
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.5 4.5 7 10l5.5 5.5" />
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.tabArrow} ${styles.tabArrowForward}`}
        onClick={() => scrollBy(100)}
        tabIndex={-1}
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7.5 4.5 13 10l-5.5 5.5" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recommended filters — Flutter FilterRecommendedContainer + CustomJobListSortByFilterWidget
// ---------------------------------------------------------------------------

function RecommendedFilters({
  aggregations,
  filters,
  preferences,
  isLoading,
  isMobile,
  actions,
}: {
  aggregations: ReturnType<typeof useViewJobs>['state']['recommendedAggregations'];
  filters: ReturnType<typeof useViewJobs>['state']['recommendedFilters'];
  preferences: Record<string, unknown> | null;
  isLoading: boolean;
  isMobile: boolean;
  actions: ReturnType<typeof useViewJobs>['actions'];
}) {
  const { t } = useTranslation();
  const withState = useMemo(() => withCheckedState(aggregations, filters), [aggregations, filters]);
  const supportedKeys = getRecommendedFilterKeys(preferences);
  const filterAggregations = withState.filter((a) => supportedKeys.includes(a.name ?? ''));
  const sortAggregation = withState.find((a) => a.name === 'sortBy');

  if (isLoading && aggregations.length === 0) {
    return (
      <div className={styles.filterCard} aria-hidden="true">
        <span className={`${styles.shimmerLine} ${styles.shimmerChip}`} />
        <span className={`${styles.shimmerLine} ${styles.shimmerChip}`} />
        <span className={`${styles.shimmerLine} ${styles.shimmerChip}`} />
        <span className={`${styles.shimmerLine} ${styles.shimmerChip}`} />
      </div>
    );
  }

  const filterDropdowns = filterAggregations.map((agg) => (
    <FilterDropdown
      key={agg.name}
      aggregation={agg}
      onChange={actions.onRecommendedFilterSelection}
      onApply={actions.onApplyRecommendedFilters}
      onClear={actions.onClearRecommendedFilter}
    />
  ));

  const applyButton = (
    <button
      type="button"
      className={`${styles.filterActionBtn} ${styles.filterApply}`}
      onClick={actions.onApplyRecommendedFilters}
    >
      {t('Apply Filters')}
    </button>
  );

  // Flutter isMobile: one scrolling row (Filters:, filters, Apply Filters) + ClearFilterIconButton,
  // no "Clear all"; the sort control sits below
  if (isMobile) {
    return (
      <div className={`${styles.filterCard} ${styles.filterCardMobile}`}>
        <div className={styles.filterMobileRow}>
          <div className={styles.filterScroll}>
            <span className={styles.filterLabel}>{t('Filters:')}</span>
            {filterDropdowns}
            {applyButton}
          </div>
          <button
            type="button"
            className={styles.clearIconBtn}
            onClick={actions.onClearRecommendedFilters}
            aria-label={t('Clear all')}
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" />
            </svg>
          </button>
        </div>
        {sortAggregation && (
          <SortDropdown aggregation={sortAggregation} onSelect={actions.onSelectRecommendedSort} />
        )}
      </div>
    );
  }

  return (
    <div className={styles.filterCard}>
      <span className={styles.filterLabel}>{t('Filters:')}</span>
      <div className={styles.filterChips}>
        {filterDropdowns}
      </div>
      <div className={styles.filterActions}>
        {sortAggregation && <SortDropdown aggregation={sortAggregation} onSelect={actions.onSelectRecommendedSort} />}
        <button type="button" className={styles.filterActionBtn} onClick={actions.onClearRecommendedFilters}>
          {t('Clear all')}
        </button>
        {applyButton}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload resume — Flutter WebRecommendedJobs showUploadResume + _browseFileButton
// ---------------------------------------------------------------------------

function UploadResumePrompt({
  isUploading,
  onUpload,
}: {
  isUploading: boolean;
  onUpload: (file: File) => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDataPrivacy, setShowDataPrivacy] = useState(false);

  const pickFile = () => fileInputRef.current?.click();

  const handleBrowse = () => {
    if (isAgreedWithDataPolicy()) {
      void analyticsService.logEvent('career_page', 'click_on_upload_resume');
      pickFile();
      return;
    }
    setShowDataPrivacy(true);
  };

  const handleAgree = () => {
    setShowDataPrivacy(false);
    setAgreedWithDataPolicy();
    pickFile();
  };

  return (
    <div className={styles.uploadCard} aria-busy={isUploading}>
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_ACCEPT_STRING}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onUpload(file);
        }}
      />
      {showDataPrivacy && (
        <DataPrivacyDialog onAgree={handleAgree} onCancel={() => setShowDataPrivacy(false)} />
      )}

      <h2 className={styles.uploadTitle}>{t(' Upload resume to view recommended jobs').trim()}</h2>
      {/* Flutter WebAssetConstants.uploadResumeIllustration */}
      <img src="/assets/icons/uploadResumeIllustration.svg" alt="" className={styles.uploadIllustration} />
      <button type="button" className={styles.uploadBtn} onClick={handleBrowse} disabled={isUploading}>
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13V3.5M6 7.5l4-4 4 4M4 16.5h12" />
        </svg>
        {t('Browse File')}
      </button>
      <p className={styles.uploadHint}>
        {t('We accept ')}
        <strong>DOC, DOCX, TXT, PDF, WPS</strong> {t('less than')} <strong>5MB</strong>
      </p>
      {/* Flutter ModalProgressHUD (isRecommendedJobLoading) while the resume uploads */}
      {isUploading && (
        <div className={styles.uploadBusy} role="status">
          <span className={styles.busySpinner} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state — Flutter NoJobFoundWidget
// ---------------------------------------------------------------------------

function EmptyState({ text }: { text: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className={styles.empty}>
      <img src="/assets/icons/noRecommendedJobs.svg" alt="" className={styles.emptyImage} />
      <p className={styles.emptyText}>{t(text)}</p>
      <button
        type="button"
        className={styles.homeBtn}
        onClick={() => {
          // Flutter navigateTo(CoreRoute.homeRoute, isClearStack: true)
          void analyticsService.logScreenView({ screenName: ROUTES.HOME });
          navigate(ROUTES.HOME, { replace: true });
        }}
      >
        {t('Go to homepage')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shimmer — Flutter JobsListViewShimmer
// ---------------------------------------------------------------------------

function JobListShimmer({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={styles.shimmerCard} aria-hidden="true">
          <span className={`${styles.shimmerLine} ${styles.shimmerTitle}`} />
          <span className={`${styles.shimmerLine} ${styles.shimmerRow}`} />
          <span className={`${styles.shimmerLine} ${styles.shimmerRow}`} />
          <span className={`${styles.shimmerLine} ${styles.shimmerRowShort}`} />
          <div className={styles.shimmerFooter}>
            <span className={`${styles.shimmerLine} ${styles.shimmerPill}`} />
            <span className={`${styles.shimmerLine} ${styles.shimmerBtn}`} />
          </div>
        </div>
      ))}
    </>
  );
}
